



































































































































































































import {
  computed,
  defineComponent,
  onBeforeMount,
  ref,
  watch,
} from "@nuxtjs/composition-api";
import { SfCheckbox } from "@storefront-ui/vue";
import omitDeep from "omit-deep";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { min, oneOf, required } from "vee-validate/dist/rules";
import { PropType } from "vue";
import JetInput from "~/components/JetUi/atoms/Input/JetInput.vue";
import JetUiButton from "~/components/JetUi/atoms/JetUiButton/JetUiButton.vue";
import SfSelect from "~/components/JetUi/molecules/SfSelect/SfSelect.vue";
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";
import type { Countries, Country, UseCountrySearchParams } from "~/composables";
import { CountryCodeEnum, useCountrySearch } from "~/composables";
import type { TransformedCustomerAddress } from "~/modules/customer/composables/types";
import addressGetter from "~/modules/customer/getters/addressGetter";

extend("required", {
  ...required,
  message: "This field is required",
});

extend("min", {
  ...min,
  message: "The field should have at least {length} characters",
});

extend("oneOf", {
  ...oneOf,
  message: "Invalid country",
});

export default defineComponent({
  name: "AddressForm",

  components: {
    JetInput,
    JetUiButton,
    SfSelect,
    SfCheckbox,
    ValidationProvider,
    ValidationObserver,
    SkeletonLoader,
  },

  props: {
    address: {
      type: Object as PropType<TransformedCustomerAddress>,
      default: () => ({
        apartment: "",
        city: "",
        country_code: "" as CountryCodeEnum,
        firstname: "",
        lastname: "",
        postcode: "",
        region: {
          region_code: "",
          region_id: 0,
        },
        street: "",
        telephone: "",
        default_shipping: false,
        default_billing: false,
      }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { load: loadCountries, search: searchCountry } = useCountrySearch();

    const form = ref<TransformedCustomerAddress | null>(null);
    const formLoading = ref<boolean>(false);

    const countries = ref<Countries[]>([]);
    const countriesList = computed(() =>
      addressGetter.countriesList(countries.value)
    );
    const country = ref<Country | null>(null);
    const regionInformation = computed(() =>
      addressGetter.regionList(country.value)
    );
    const zipCodeError = ref<string | null>(null);

    const updateCountry = async (params: UseCountrySearchParams) => {
      country.value = await searchCountry(params);
      form.value.region = { region_id: null, region_code: "" };
    };

    watch(
      () => props.address,
      () => {
        form.value = {
          apartment: props.address.apartment,
          city: props.address.city,
          country_code: props.address.country_code,
          firstname: props.address.firstname,
          lastname: props.address.lastname,
          postcode: props.address.postcode,
          region: {
            region_code: "",
            region_id: null,
            ...props.address.region,
          },
          street: props.address.street,
          // Remove from telephone all non-numeric characters
          telephone: props.address.telephone.replace(/\D/g, ""),
          default_shipping: props.address.default_shipping || false,
          default_billing: props.address.default_billing || false,
        } as unknown as TransformedCustomerAddress;
      },
      { immediate: true }
    );

    const fetchAddressByZipCode = async (zipCode: string) => {
      if (zipCode.length !== 8) {
        zipCodeError.value = "O CEP deve conter 8 dígitos.";
        return;
      }
      zipCodeError.value = null;
      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${zipCode}/json/`
        );
        const data = await response.json();
        if (data.erro) {
          zipCodeError.value = "CEP não encontrado.";
          return;
        }
        form.value.street = data.logradouro;
        form.value.city = data.localidade;
        form.value.region.region_code = data.uf;
        form.value.apartment = "";
      } catch {
        zipCodeError.value =
          "Erro ao buscar o endereço. Tente novamente mais tarde.";
      }
    };

    const submitForm = () => {
      const regionId = regionInformation.value.find(
        (r) => r.abbreviation === form.value.region.region_code
      )?.id;
      form.value.region = regionId
        ? { region_id: regionId }
        : {
            ...form.value.region,
            region_code: "",
            region_id: null,
          };

      form.value.telephone = form.value.telephone.replace(/\D/g, "");

      emit("submit", {
        form: omitDeep(form.value, ["__typename"]),
        onError: () => {},
      });
    };

    onBeforeMount(async () => {
      formLoading.value = true;
      countries.value = await loadCountries();
      const brazil = countries.value.find(
        (country) => country.two_letter_abbreviation === "BR"
      );
      if (brazil) {
        form.value.country_code =
          brazil.two_letter_abbreviation as CountryCodeEnum;
        country.value = await searchCountry({ id: brazil.id });
      } else if (props.address.country_code) {
        country.value = await searchCountry({ id: props.address.country_code });
      }
      formLoading.value = false;
    });

    const changeField = (field: string, value: string) => {
      form.value[field] = value;
    };

    return {
      form,
      submitForm,
      countriesList,
      regionInformation,
      updateCountry,
      fetchAddressByZipCode,
      changeField,
      formLoading,
    };
  },
});
